import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  effect,
  inject,
  type OnDestroy,
  type OnInit,
  PLATFORM_ID,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule } from '@angular/material/menu';
import { AuthService } from '../../services/auth.service';
import { CmsService } from '../../services/cms.service';
import { UtilService } from '../../services/util.service';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { MatDividerModule } from '@angular/material/divider';
import { StorageService } from '../../services/storage.service';
import { StorageEnum } from '../../enums/storage.enum';
import { Nav } from '../../enums/nav.enum';
import { type UserProfile } from '../../models/user-profile';
import { type AccountDetail } from '../../models/account-detail';
import { CustomerClassEnum } from '../../enums/customerclass.enum';
import { isPlatformBrowser } from '@angular/common';
import { environment } from '../../../../environments/environment';
import { catchError, filter, of } from 'rxjs';
import { type NavState, type MenuItem, type NavLabels } from '../../models/nav-state';

@Component({
  selector: 'app-menu',
  imports: [MatMenuModule, MatButtonModule, RouterModule, MatDividerModule],
  templateUrl: './menu.component.html',
  styleUrl: './menu.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MenuComponent implements OnInit, OnDestroy {
  private readonly authS = inject(AuthService);
  private readonly cmsS = inject(CmsService);
  private readonly router = inject(Router);
  private readonly storageS = inject(StorageService);
  private readonly utilsS = inject(UtilService);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly destroyRef = inject(DestroyRef);

  pageLabels = signal<NavLabels | null>(null);
  billingMenuOpen = signal(false);
  manageMenuOpen = signal(false);
  readonly navState = signal<NavState>(this.getDefaultNavState());
  showHeader = signal(false);
  env = environment;
  isBrowser = isPlatformBrowser(this.platformId);
  isMobile = signal(false);
  isLoggedIn = signal(false);

  /** Bound resize handler for cleanup */
  private readonly resizeHandler = (): void => {
    this.isMobile.set(this.utilsS.isMobile(992));
  };

  constructor() {
    effect(() => {
      if (this.utilsS.updateNav()) {
        this.navState.update((state) => this.updateNavState(state));
      }
    });
  }

  ngOnInit(): void {
    if (this.isBrowser) {
      this.isMobile.set(this.utilsS.isMobile(992));
      window.addEventListener('resize', this.resizeHandler);
    }

    // Handle initial route immediately (before CMS loads)
    this.handleNavigation(this.router.url);

    // Subscribe to future navigation events with cleanup
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((event) => {
        this.handleNavigation(event.urlAfterRedirects);
      });

    // Load CMS content with error handling
    this.cmsS
      .getContent('oas-nav', 'Nav')
      .pipe(
        catchError((error) => {
          console.error('[MenuComponent] Failed to load nav content:', error);
          return of(null);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((data) => {
        if (data === null) {
          return;
        }

        const rawLabels = this.cmsS.flattenBuilderReferencesPreservingKeys(data);

        const billingOptions = (rawLabels.billingAndPaymentsMenuOptions as MenuItem[]) ?? [];
        const billingLen = Math.ceil(billingOptions.length / 2);
        const billingSection1 = billingOptions.slice(0, billingLen);
        const billingSection2 = billingOptions.slice(billingLen);

        const manageOptions = (rawLabels.manageAccountMenuOptions as MenuItem[]) ?? [];
        const manageLen = Math.ceil(manageOptions.length / 2);
        const manageSection1 = manageOptions.slice(0, manageLen);
        const manageSection2 = manageOptions.slice(manageLen);

        this.pageLabels.set({
          ...rawLabels,
          billingAndPaymentsMenuOptions: [billingSection1, billingSection2],
          manageAccountMenuOptions: [manageSection1, manageSection2],
        } satisfies NavLabels);
      });
  }

  ngOnDestroy(): void {
    if (this.isBrowser) {
      window.removeEventListener('resize', this.resizeHandler);
    }
  }

  private handleNavigation(url: string): void {
    if (this.utilsS.isNullOrUndefined(this.storageS.getSession(StorageEnum.LoginResponse))) {
      this.isLoggedIn.set(false);
    } else {
      this.isLoggedIn.set(true);
    }
    const newState = this.getDefaultNavState();
    switch (true) {
      case url.includes(Nav.Login):
      case url.includes(Nav.Register):
      case url.includes(Nav.RegisterAccount):
      case url.includes(Nav.Forgot):
      case url.includes(Nav.OneTimePayment):
      case url.includes(Nav.OneTimePaymentSuccess):
      case url.includes(Nav.OneTimePaymentError):
      case url.includes(Nav.AccountLookup):
      case url.includes(Nav.Outage):
      case url.includes(Nav.Reset):
      case url.includes(Nav.AppError):
        newState.ShowNav = false;
        this.showHeader.set(false);
        break;
      case url.includes(Nav.Advantage):
        if (!this.isLoggedIn()) {
          newState.ShowNav = true;
          this.showHeader.set(true);
          newState.Logo.Url = Nav.Login;
          newState.Logo.Aria = 'Gas South 20 Years, go to Login';
          newState.Home.Show = false;
          newState.Billing.Show = false;
          newState.Manage.Show = false;
          newState.PayBill.Show = false;
          newState.Perks.Show = false;
          newState.Logout = false;
          newState.Login = true;
        } else {
          newState.ShowNav = true;
          this.showHeader.set(true);
          newState.Logo.Url = Nav.Login;
          newState.Logo.Aria = 'Gas South 20 Years, go to Login';
          newState.Home.Show = true;
          newState.Billing.Show = true;
          newState.Manage.Show = true;
          newState.PayBill.Show = true;
          newState.Perks.Show = true;
          newState.Logout = true;
          newState.Login = false;
        }
        break;
      case url.includes(Nav.UnauthenticatedRateRenewal):
      case url.includes(Nav.UnauthenticatedRateRenewalError):
      case url.includes(Nav.UnauthenticatedRateRenewalSuccess):
        newState.ShowNav = true;
        this.showHeader.set(true);
        newState.Logo.Url = Nav.Login;
        newState.Logo.Aria = 'Gas South 20 Years, go to Login';
        newState.Home.Show = false;
        newState.Billing.Show = false;
        newState.Manage.Show = false;
        newState.PayBill.Show = false;
        newState.Perks.Show = false;
        newState.Logout = false;
        newState.Login = false;
        break;
      case url.includes(Nav.UnauthenticatedPaperless):
      case url.includes(Nav.UnauthenticatedPaperlessError):
      case url.includes(Nav.UnauthenticatedPaperlessSuccess):
        newState.ShowNav = true;
        this.showHeader.set(true);
        newState.Logo.Url = Nav.Login;
        newState.Logo.Aria = 'Gas South 20 Years, go to Login';
        newState.Home.Show = false;
        newState.Billing.Show = false;
        newState.Manage.Show = false;
        newState.PayBill.Show = false;
        newState.Perks.Show = false;
        newState.Logout = false;
        newState.Login = false;
        break;
      case url.includes(Nav.MultiAccountSummary):
        newState.ShowNav = true;
        this.showHeader.set(true);
        newState.Logo.Url = Nav.MultiAccountSummary;
        newState.Logo.Aria = 'Gas South 20 Years, go to multi account summary page';
        newState.Home.Show = false;
        newState.Billing.Show = false;
        newState.Manage.Show = false;
        newState.PayBill.Show = false;
        newState.Perks.Show = false;
        break;
      case url.includes(Nav.Closed):
      case url.includes(Nav.Seasonal):
        newState.ShowNav = true;
        this.showHeader.set(true);
        newState.Home.Show = false;
        newState.Billing.Show = false;
        newState.Manage.Show = false;
        newState.PayBill.Show = false;
        newState.Perks.Show = false;
        newState.Logo.Url = Nav.Login;
        newState.Logo.Aria = 'Gas South 20 Years, go to Login';
        break;
      case url.includes(Nav.ReconnectService):
      case url.includes(Nav.ReconnectRates):
      case url.includes(Nav.ReconnectSuccess):
      case url.includes(Nav.ReconnectError):
        newState.ShowNav = true;
        this.showHeader.set(true);
        newState.Home.Show = false;
        newState.Billing.Show = false;
        newState.Manage.Show = false;
        newState.PayBill.Show = false;
        newState.Perks.Show = false;
        break;
      case url.includes(Nav.BioTerms):
        newState.ShowNav = true;
        this.showHeader.set(false);
        newState.Home.Show = false;
        newState.Billing.Show = false;
        newState.Manage.Show = false;
        newState.PayBill.Show = false;
        newState.Perks.Show = false;
        break;
      default:
        newState.ShowNav = true;
        this.showHeader.set(true);
        break;
    }

    // Hide auth-dependent elements during SSR to prevent hydration mismatches
    if (!this.isBrowser) {
      newState.Home.Show = false;
      newState.Billing.Show = false;
      newState.Manage.Show = false;
      newState.PayBill.Show = false;
      newState.Perks.Show = false;
      newState.Logout = false;
      newState.Login = false;
    }

    // Only mutate environment in browser to prevent SSR cross-request state leakage
    // TODO: Refactor to use a service signal for navMargin state
    if (this.isBrowser) {
      this.env.navMargin = newState.ShowNav;
    }
    this.navState.set(this.updateNavState(newState));
  }

  updateNavState(newState: NavState): NavState {
    if (newState.ShowNav) {
      const accDet: AccountDetail = this.storageS.getSession(StorageEnum.AccountDetail);
      const loginRes: UserProfile = this.storageS.getSession(StorageEnum.LoginResponse);

      const loginAcc = loginRes?.Accounts[0];
      const accSA = loginAcc?.ServiceAddresses[0];

      if (accSA?.SaStatusFlag === '10') {
        newState.PayBill.Show = false;
      }

      if ((loginAcc?.NumAgreements ?? 1) > 1) {
        newState.Manage.PlanDetails = false;
        newState.Manage.MultiSAPlanDetails = true;
      }

      // do not show Plan details if SaRate is null - no rates to show
      if (accDet) {
        if (this.utilsS.isNullOrWhitespace(accDet.SaRateScheduleCode)) {
          newState.Manage.PlanDetails = false;
        }
      }

      if (
        accDet?.PrimaryAccountHolder &&
        (accDet?.CustomerClass === CustomerClassEnum.RES || accDet?.CustomerClass === CustomerClassEnum.SMLCOM) &&
        loginAcc?.CGSAcct === false
      ) {
        newState.Manage.Alerts = true;
      }

      if (accDet?.CustomerClass === CustomerClassEnum.RES || accDet?.CustomerClass === CustomerClassEnum.SMLCOM) {
        newState.Manage.RAF = true;
      }

      if (loginRes?.TotalNumAccounts > 1) {
        newState.Manage.MultiAccount = true;
      }
    }
    return newState;
  }

  /**
   * Determines whether a specific menu option should be displayed.
   * @param optionID - The unique identifier of the menu option
   * @returns true if the option should be shown, false otherwise
   */
  showMenuOption(optionID: string | undefined): boolean {
    if (!optionID) {
      return true;
    }
    const state = this.navState();
    switch (optionID) {
      case 'Wallet':
        return state.Billing.Wallet;
      case 'BillDelivery':
        return state.Billing.BillDelivery;
      case 'AutoPay':
        return state.Billing.AutoPay;
      case 'PaymentHistory':
        return state.Billing.PaymentHistory;
      case 'BillingHistory':
        return state.Billing.BillingHistory;
      case 'PlanDetails':
        return state.Manage.PlanDetails;
      case 'MultiSAPlanDetails':
        return state.Manage.MultiSAPlanDetails;
      case 'ContactInfo':
        return state.Manage.ContactInfo;
      case 'Alerts':
        return state.Manage.Alerts;
      case 'RAF':
        return state.Manage.RAF;
      case 'ManageAccount':
        return state.Manage.Manage;
      case 'MultiAccount':
        return state.Manage.MultiAccount;
      default:
        return true;
    }
  }

  /**
   * Gets billing menu options for a specific section.
   * @param section - The section index (0 for left column, 1 for right column)
   * @returns Array of menu items for the requested section
   */
  getBillingMenuOptions(section: 0 | 1): MenuItem[] {
    return this.pageLabels()?.billingAndPaymentsMenuOptions[section] ?? [];
  }

  /**
   * Gets manage account menu options for a specific section.
   * @param section - The section index (0 for left column, 1 for right column)
   * @returns Array of menu items for the requested section
   */
  getManageMenuOptions(section: 0 | 1): MenuItem[] {
    return this.pageLabels()?.manageAccountMenuOptions[section] ?? [];
  }

  /**
   * Creates the default navigation state with all menu options visible.
   * @returns The default NavState configuration
   */
  getDefaultNavState(): NavState {
    return {
      ShowNav: false,
      Logo: {
        Show: true,
        Url: Nav.AccountSummary,
        Aria: 'Gas South 20 Years, go to account summary page',
      },
      Home: {
        Show: true,
        Url: Nav.AccountSummary,
        Aria: 'Go to account summary Home page',
      },
      Billing: {
        Show: true,
        Wallet: true,
        BillDelivery: true,
        AutoPay: true,
        PaymentHistory: true,
        BillingHistory: true,
      },
      Manage: {
        Show: true,
        PlanDetails: true,
        MultiSAPlanDetails: false,
        ContactInfo: true,
        Alerts: false,
        RAF: false,
        Manage: true,
        MultiAccount: false,
      },
      PayBill: {
        Show: true,
        Url: Nav.Payment,
        Aria: 'Go to pay bill payment page',
      },
      Perks: {
        Show: true,
        Url: Nav.Advantage,
        Aria: 'Go to perks advantage page',
      },
      Logout: true,
      Login: false,
    };
  }

  logout(): void {
    this.authS.logout();
  }
}
